/**
 * The Learning screen makes three job-scoped reads beside the acceleration report. When one of them
 * fails the screen says so rather than showing its absence as a zero — the right instinct, but the
 * red bar named nothing, so an operator (and an engineer with a log) could not tell which answer was
 * missing or whether the numbers beside it were affected. The first customer run met that bar on a
 * healthy job for a whole session, 2026-09-20.
 */
export const learningReadNames = {
  accuracy: 'How often this job is already right',
  faults: 'Recurring corrections',
  suggestions: 'Suggestions',
} as const;

export type LearningRead = keyof typeof learningReadNames;

/** The reads that failed, in the order the screen lists them. */
export function failedLearningReads(
  reads: Readonly<Record<LearningRead, 'loading' | 'ready' | 'error'>>,
): LearningRead[] {
  return (Object.keys(learningReadNames) as LearningRead[]).filter(
    (read) => reads[read] === 'error',
  );
}

/** What the bar says: which reads are missing, and what is not being concluded from that. */
export function learningReadFailureLine(failed: readonly LearningRead[]): string | null {
  if (!failed.length) return null;
  const names = failed.map((read) => learningReadNames[read]);
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list} could not be read. No missing result is being treated as “nothing learned.”`;
}
